import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  Filter,
  RefreshCw,
  UserCheck,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Eye,
  Edit,
  UserPlus,
  ChevronDown,
  Check,
  AlertCircle,
  Building2,
  Briefcase,
  Send,
  UserCircle,
  Activity,
  CheckCircle,
} from "lucide-react";
import toast from "react-hot-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadCandidateManagement = ({ onAddCandidate, onCandidateUpdated }) => {
  const [candidates, setCandidates] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedRecruiter, setSelectedRecruiter] = useState("");
  const [candidateSubmissions, setCandidateSubmissions] = useState([]);
  const [matchingJobs, setMatchingJobs] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchCandidates(), fetchRecruiters()]);
    setLoading(false);
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not wrapped in {candidates: [...]}
        const candidatesList = Array.isArray(data)
          ? data
          : data.candidates || [];
        setCandidates(candidatesList);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
      toast.error("Failed to load candidates");
    }
  };

  const fetchRecruiters = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        // Filter for users with recruiter role
        const recruiterUsers = (data.users || []).filter(
          (u) => u.role === "recruiter" || u.user_type === "recruiter"
        );
        setRecruiters(recruiterUsers);
      }
    } catch (error) {
      console.error("Error fetching recruiters:", error);
    }
  };

  const handleAssignRecruiter = async () => {
    if (!selectedRecruiter || !selectedCandidate) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/candidates/${selectedCandidate.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            primary_recruiter_id: selectedRecruiter,
            primary_recruiter_lock_until: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000
            ).toISOString(), // 30 days lock
          }),
        }
      );

      if (response.ok) {
        toast.success("Primary recruiter assigned successfully!");
        setShowAssignModal(false);
        setSelectedCandidate(null);
        setSelectedRecruiter("");
        fetchCandidates();
      } else {
        toast.error("Failed to assign recruiter");
      }
    } catch (error) {
      console.error("Error assigning recruiter:", error);
      toast.error("Error assigning recruiter");
    }
  };

  const openAssignModal = (candidate) => {
    setSelectedCandidate(candidate);
    setSelectedRecruiter(candidate.primary_recruiter_id || "");
    setShowAssignModal(true);
  };

  const openViewModal = async (candidate, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Fetch fresh candidate data
    try {
      const candResponse = await fetch(
        `${BACKEND_URL}/api/candidates/${candidate.id}`
      );
      if (candResponse.ok) {
        const freshCandidate = await candResponse.json();
        setSelectedCandidate(freshCandidate);
      } else {
        setSelectedCandidate(candidate); // Fallback to cached data
      }
    } catch (error) {
      console.error("Error fetching fresh candidate data:", error);
      setSelectedCandidate(candidate); // Fallback to cached data
    }

    setShowViewModal(true);

    // Fetch submissions for this candidate
    try {
      const [submissionsResponse, jobsResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/submissions`),
        fetch(`${BACKEND_URL}/api/jobs`),
      ]);

      let candidateSubms = [];
      let submittedJobIds = [];

      if (submissionsResponse.ok) {
        const data = await submissionsResponse.json();
        const submissions = data.submissions || [];
        // Filter submissions for this candidate
        candidateSubms = submissions.filter(
          (s) =>
            s.candidate_id === candidate.id ||
            s.candidate_email === candidate.email ||
            s.candidate_name === candidate.name
        );
        setCandidateSubmissions(candidateSubms);

        // Get job IDs already submitted to
        submittedJobIds = candidateSubms.map((s) => s.job_id);
      }

      // Fetch and filter matching open jobs
      if (jobsResponse.ok) {
        const jobsData = await jobsResponse.json();
        const allJobs = jobsData.jobs || [];

        // Get candidate skills as array
        const candidateSkills = (candidate.skills || "")
          .toLowerCase()
          .split(/[\s,]+/)
          .filter((s) => s.length > 2);
        const candidateTitle = (candidate.current_title || "").toLowerCase();

        // Filter for open jobs that match candidate skills/title and haven't been submitted to
        const matches = allJobs.filter((job) => {
          // Must be open
          if (job.status !== "open") return false;

          // Must not be already submitted to
          if (submittedJobIds.includes(job.id)) return false;

          const jobTitle = (job.title || "").toLowerCase();
          const jobSkills = (job.required_skills || "").toLowerCase();

          // Match by title similarity
          const titleMatch =
            candidateSkills.some(
              (skill) => jobTitle.includes(skill) || jobSkills.includes(skill)
            ) || jobTitle.includes(candidateTitle);

          return titleMatch;
        });

        setMatchingJobs(matches);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const filteredCandidates = candidates.filter((c) => {
    const matchesSearch =
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.current_title?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || c.candidate_status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const getRecruiterName = (recruiterId) => {
    const recruiter = recruiters.find((r) => r.id === recruiterId);
    return recruiter ? recruiter.name || recruiter.email : "Unassigned";
  };

  const getLockStatus = (candidate) => {
    if (!candidate.primary_recruiter_lock_until) return null;
    const lockDate = new Date(candidate.primary_recruiter_lock_until);
    const now = new Date();
    const isLocked = lockDate > now;
    const daysRemaining = Math.ceil((lockDate - now) / (1000 * 60 * 60 * 24));
    return { isLocked, daysRemaining };
  };

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search candidates by name, email, or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="passive">Passive</option>
              <option value="placed">Placed</option>
              <option value="inactive">Inactive</option>
            </select>
            {onAddCandidate && (
              <button
                onClick={onAddCandidate}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                Add Candidate
              </button>
            )}
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              title="Refresh candidates"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Showing {filteredCandidates.length} of {candidates.length} candidates
          from your team recruiters
        </p>
      </div>

      {/* Candidates Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Current Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Primary Recruiter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Added
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Candidate Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : filteredCandidates.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No candidates found
                  </td>
                </tr>
              ) : (
                filteredCandidates.map((candidate) => {
                  const lockStatus = getLockStatus(candidate);
                  return (
                    <tr key={candidate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {candidate.name?.charAt(0) || "C"}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {candidate.name || "N/A"}
                            </p>
                            <p className="text-sm text-gray-500">
                              {candidate.email || "N/A"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {candidate.current_title || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {candidate.location || "N/A"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-gray-900">
                            {candidate.primary_recruiter_name || "Unassigned"}
                          </div>
                          {lockStatus && lockStatus.isLocked && (
                            <div className="flex items-center gap-1 text-xs text-green-600">
                              <Check className="h-3 w-3" />
                              Locked for {lockStatus.daysRemaining} days
                            </div>
                          )}
                          {!candidate.primary_recruiter_id && (
                            <div className="text-xs text-orange-600">
                              Not assigned
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {candidate.created_at
                          ? new Date(candidate.created_at).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            candidate.candidate_status === "available"
                              ? "bg-green-100 text-green-800"
                              : candidate.candidate_status === "screened"
                              ? "bg-blue-100 text-blue-800"
                              : candidate.candidate_status === "sourced"
                              ? "bg-purple-100 text-purple-800"
                              : candidate.candidate_status === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {candidate.candidate_status || "sourced"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={(e) => openViewModal(candidate, e)}
                          className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                          title="View candidate details and submission history"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Recruiter Modal */}
      {showAssignModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Assign Primary Recruiter
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Assign a primary recruiter to {selectedCandidate.name}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Recruiter <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedRecruiter}
                  onChange={(e) => setSelectedRecruiter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">-- Select a recruiter --</option>
                  {recruiters.map((recruiter) => (
                    <option key={recruiter.id} value={recruiter.id}>
                      {recruiter.name || recruiter.email} ({recruiter.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Assigning a primary recruiter will lock
                  this candidate to them for 30 days. During this period, only
                  the assigned recruiter can work with this candidate.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedCandidate(null);
                  setSelectedRecruiter("");
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRecruiter}
                disabled={!selectedRecruiter}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Assign Recruiter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Details View Modal */}
      {showViewModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-purple-600 font-bold text-2xl">
                      {selectedCandidate.name?.charAt(0) || "C"}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {selectedCandidate.name || "N/A"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedCandidate.current_title || "N/A"} at{" "}
                      {selectedCandidate.current_company || "N/A"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedCandidate(null);
                    setCandidateSubmissions([]);
                    setMatchingJobs([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <UserCircle className="h-5 w-5 text-purple-600" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      {selectedCandidate.email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone</label>
                    <p className="font-medium flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {selectedCandidate.phone || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Location</label>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      {selectedCandidate.location || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <p>
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          selectedCandidate.candidate_status === "active"
                            ? "bg-green-100 text-green-800"
                            : selectedCandidate.candidate_status === "passive"
                            ? "bg-yellow-100 text-yellow-800"
                            : selectedCandidate.candidate_status === "placed"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {selectedCandidate.candidate_status || "active"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Candidate Status Update */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-600" />
                  Candidate Status
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600 mb-2 block">
                      Current Status
                    </label>
                    <span
                      className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${
                        selectedCandidate.candidate_status === "available"
                          ? "bg-green-100 text-green-800"
                          : selectedCandidate.candidate_status === "screened"
                          ? "bg-blue-100 text-blue-800"
                          : selectedCandidate.candidate_status === "sourced"
                          ? "bg-purple-100 text-purple-800"
                          : selectedCandidate.candidate_status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {selectedCandidate.candidate_status || "sourced"}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Update Status
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {[
                        {
                          value: "sourced",
                          label: "Sourced",
                          icon: "🔍",
                          color: "purple",
                        },
                        {
                          value: "screened",
                          label: "Screened",
                          icon: "✅",
                          color: "blue",
                        },
                        {
                          value: "available",
                          label: "Available",
                          icon: "🟢",
                          color: "green",
                        },
                        {
                          value: "rejected",
                          label: "Rejected",
                          icon: "❌",
                          color: "red",
                        },
                        {
                          value: "unavailable",
                          label: "Unavailable",
                          icon: "🔒",
                          color: "gray",
                        },
                      ].map((status) => (
                        <button
                          key={status.value}
                          onClick={async () => {
                            try {
                              const response = await fetch(
                                `${BACKEND_URL}/api/candidates/${selectedCandidate.id}`,
                                {
                                  method: "PUT",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify({
                                    candidate_status: status.value,
                                  }),
                                }
                              );
                              if (response.ok) {
                                toast.success(
                                  `Status updated to ${status.label}`
                                );
                                // Update local state
                                setSelectedCandidate({
                                  ...selectedCandidate,
                                  candidate_status: status.value,
                                });
                                // Refresh candidates list
                                fetchCandidates();
                              }
                            } catch (error) {
                              toast.error("Failed to update status");
                            }
                          }}
                          className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                            selectedCandidate.candidate_status === status.value
                              ? "border-purple-500 bg-purple-50"
                              : "border-gray-200 hover:border-purple-300 hover:bg-gray-50"
                          }`}
                        >
                          <span className="text-xl">{status.icon}</span>
                          <span className="text-xs font-medium text-gray-700">
                            {status.label}
                          </span>
                          {selectedCandidate.candidate_status ===
                            status.value && (
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Primary Recruiter */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-purple-600" />
                  Primary Recruiter
                </h4>

                {!selectedCandidate.primary_recruiter_id ? (
                  // Unassigned - Show assign option
                  <div className="space-y-3">
                    <p className="text-orange-600 font-medium">Not assigned</p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Primary Recruiter
                      </label>
                      <select
                        value={selectedRecruiter}
                        onChange={(e) => setSelectedRecruiter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Select a recruiter...</option>
                        {recruiters.map((recruiter) => (
                          <option key={recruiter.id} value={recruiter.id}>
                            {recruiter.full_name ||
                              recruiter.name ||
                              recruiter.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={async () => {
                        if (!selectedRecruiter) {
                          toast.error("Please select a recruiter");
                          return;
                        }
                        try {
                          const response = await fetch(
                            `${BACKEND_URL}/api/candidates/${selectedCandidate.id}`,
                            {
                              method: "PUT",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                primary_recruiter_id: selectedRecruiter,
                                primary_recruiter_lock_until: new Date(
                                  Date.now() + 30 * 24 * 60 * 60 * 1000
                                ).toISOString(),
                              }),
                            }
                          );
                          if (response.ok) {
                            toast.success(
                              "Primary recruiter assigned successfully!"
                            );
                            setShowViewModal(false);
                            fetchData();
                          } else {
                            toast.error("Failed to assign recruiter");
                          }
                        } catch (error) {
                          console.error("Error assigning recruiter:", error);
                          toast.error("Error assigning recruiter");
                        }
                      }}
                      disabled={!selectedRecruiter}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Assign Recruiter (30-day lock)
                    </button>
                  </div>
                ) : (
                  // Assigned - Show current recruiter with change option (Lead Lena can always change)
                  <div className="space-y-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-blue-800 font-medium mb-1">
                        Current Primary Recruiter:
                      </p>
                      <p className="font-semibold text-lg text-gray-900">
                        {selectedCandidate.primary_recruiter_name || "Unknown"}
                      </p>
                      {selectedCandidate.primary_recruiter_lock_until && (
                        <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Lock expires:{" "}
                          {new Date(
                            selectedCandidate.primary_recruiter_lock_until
                          ).toLocaleDateString()}
                          {new Date(
                            selectedCandidate.primary_recruiter_lock_until
                          ) > new Date() ? (
                            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-full">
                              Expired
                            </span>
                          )}
                        </p>
                      )}
                    </div>

                    {/* Lead Lena can always change the primary recruiter */}
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex items-center gap-2 mb-3">
                        <UserCheck className="h-5 w-5 text-purple-600" />
                        <label className="block text-sm font-medium text-gray-700">
                          Change/Reassign Primary Recruiter
                        </label>
                      </div>
                      {new Date(
                        selectedCandidate.primary_recruiter_lock_until
                      ) > new Date() && (
                        <p className="text-xs text-gray-600 mb-3 bg-gray-50 border-l-4 border-gray-300 p-2 rounded">
                          <AlertCircle className="h-3 w-3 inline mr-1" />
                          Note: As Lead, you can reassign even during an active
                          lock period. This will override the current 30-day
                          lock and create a new lock for the new recruiter.
                        </p>
                      )}
                      <select
                        value={selectedRecruiter}
                        onChange={(e) => setSelectedRecruiter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 mb-3"
                      >
                        <option value="">Select a recruiter...</option>
                        {recruiters.map((recruiter) => (
                          <option key={recruiter.id} value={recruiter.id}>
                            {recruiter.full_name ||
                              recruiter.name ||
                              recruiter.email}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={async () => {
                          if (!selectedRecruiter) {
                            toast.error("Please select a recruiter");
                            return;
                          }
                          try {
                            const response = await fetch(
                              `${BACKEND_URL}/api/candidates/${selectedCandidate.id}`,
                              {
                                method: "PUT",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  primary_recruiter_id: selectedRecruiter,
                                  primary_recruiter_lock_until: new Date(
                                    Date.now() + 30 * 24 * 60 * 60 * 1000
                                  ).toISOString(),
                                }),
                              }
                            );
                            if (response.ok) {
                              toast.success(
                                "Primary recruiter reassigned successfully!"
                              );
                              setShowViewModal(false);
                              fetchData();
                            } else {
                              toast.error("Failed to reassign recruiter");
                            }
                          } catch (error) {
                            console.error(
                              "Error reassigning recruiter:",
                              error
                            );
                            toast.error("Error reassigning recruiter");
                          }
                        }}
                        disabled={!selectedRecruiter}
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        <UserCheck className="h-4 w-4" />
                        Reassign Primary Recruiter (new 30-day lock)
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Professional Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-purple-600" />
                  Professional Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Skills</label>
                    <p className="font-medium">
                      {selectedCandidate.skills || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">
                      Years of Experience
                    </label>
                    <p className="font-medium">
                      {selectedCandidate.years_of_experience || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Education</label>
                    <p className="font-medium">
                      {selectedCandidate.education || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">
                      Engagement Type
                    </label>
                    <p className="font-medium">
                      {selectedCandidate.engagement_type || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Submission History */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Send className="h-5 w-5 text-purple-600" />
                  Submission History ({candidateSubmissions.length})
                </h4>
                {candidateSubmissions.length === 0 ? (
                  <p className="text-gray-600 text-center py-4">
                    No submissions found for this candidate
                  </p>
                ) : (
                  <div className="space-y-3">
                    {candidateSubmissions.map((submission) => (
                      <div
                        key={submission.id}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Building2 className="h-4 w-4 text-gray-400" />
                              <h5 className="font-semibold text-gray-900">
                                {submission.job_title || "N/A"}
                              </h5>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Client:</strong>{" "}
                              {submission.client_name || "N/A"}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Submitted by:</strong>{" "}
                              {submission.recruiter_name || "N/A"}
                            </p>
                            <p className="text-sm text-gray-600 mb-2">
                              <strong>Submitted on:</strong>{" "}
                              {submission.submission_date
                                ? new Date(
                                    submission.submission_date
                                  ).toLocaleDateString()
                                : "N/A"}
                            </p>
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  submission.current_status === "offer_accepted"
                                    ? "bg-green-100 text-green-800"
                                    : submission.current_status === "offer_made"
                                    ? "bg-blue-100 text-blue-800"
                                    : submission.current_status?.includes(
                                        "interview"
                                      )
                                    ? "bg-yellow-100 text-yellow-800"
                                    : submission.current_status === "rejected"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {submission.current_status
                                  ?.replace(/_/g, " ")
                                  .toUpperCase() || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Matching Open Positions */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                  Matching Open Positions ({matchingJobs.length})
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  These open jobs match the candidate's skills and haven't been
                  submitted to yet
                </p>
                {matchingJobs.length === 0 ? (
                  <div className="bg-white border border-blue-200 rounded-lg p-6 text-center">
                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">
                      No matching open positions found
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {candidateSubmissions.length > 0
                        ? "All matching positions have already been submitted to"
                        : "No open positions match this candidate's profile"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {matchingJobs.map((job) => (
                      <div
                        key={job.id}
                        className="bg-white border border-blue-200 rounded-lg p-4 hover:border-blue-400 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Briefcase className="h-4 w-4 text-blue-600" />
                              <h5 className="font-semibold text-gray-900">
                                {job.title || "N/A"}
                              </h5>
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                                Open
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                              <Building2 className="h-3 w-3 text-gray-400" />
                              <strong>Client:</strong> {job.company || "N/A"}
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Location:</strong> {job.location || "N/A"}
                            </p>
                            <p className="text-sm text-gray-600 mb-2">
                              <strong>Skills Required:</strong>{" "}
                              {job.required_skills || "N/A"}
                            </p>
                            <div className="flex items-center gap-2 mt-3">
                              <button
                                onClick={() => {
                                  toast.info(
                                    "Submission feature will open submission form"
                                  );
                                  // TODO: Open submission modal with pre-filled candidate and job data
                                }}
                                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                              >
                                <Send className="h-3 w-3" />
                                Submit Candidate
                              </button>
                              <button
                                onClick={() => {
                                  // Open job details in new tab or modal
                                  window.open(`/jobs/${job.id}`, "_blank");
                                }}
                                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
                              >
                                <Eye className="h-3 w-3" />
                                View Job
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedCandidate(null);
                  setCandidateSubmissions([]);
                  setMatchingJobs([]);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadCandidateManagement;
